import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Modal } from "react-bootstrap";
import { toast } from "sonner";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAcademicYear } from "../../core/common/context/AcademicYearContext";
import withPermission from "../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import { useEffect, useRef, useState } from "react";
import { Select } from "antd";
import { useSelector } from "react-redux";
import { createDecisionValidation } from "../../api/diplome";

type Props = {
  show: boolean;
  idDecision: string;
  validationLevel: number;
  onClose: () => void;
  onSaved?: () => void;
};

const ValidationModal = ({
  show,
  idDecision,
  validationLevel,
  onClose,
  onSaved,
}: Props) => {
  //Initialisation de l'année academmique actuelle
  const userData = useSelector((state: any) => state.auth.user);

  const [supportFile, setSupportFile] = useState<File | null>(null);

  // Références pour les fichiers
  const supportFileInputRef = useRef<HTMLInputElement>(null);

  const createSchema = z.object({
    id_decision: z.string().nonempty("La décision n'a pas été spécifiée"),
    validator: z.string().nonempty("Le validateur est requis"),
    level_of_validation: z
      .string()
      .nonempty("Le niveau de validation n'a pas été spécifié"),
    observation: z.string().nonempty("L'observation est requise"),
    statut: z.string().nonempty("Le statut est requis"),
  });

  type FormFields = z.infer<typeof createSchema>;

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      statut: "",
      validator: userData?.id,
      id_decision: idDecision,
      level_of_validation: validationLevel.toString(),
    },
  });

  const statut = watch("statut");

  //Traitement après validation du formulaire de création d'un niveau
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      if (!supportFile) {
        toast.error("Veuillez ajouter une pièce jointe");
        return;
      }

      const formData = new FormData();

      // Champs texte
      formData.append("id_decision", data.id_decision);
      formData.append("level_of_validation", data.level_of_validation);
      formData.append("observation", data.observation);
      formData.append("statut", data.statut);
      formData.append("validator", data.validator);

      // Fichier (IMPORTANT : nom = req.files attendu)
      formData.append("file", supportFile);

      //Envoi des datas pour création vers le backend
      await createDecisionValidation(
        formData,
        buildPermissionContext(Resource.DIPLOME, Action.READ),
      );

      reset(); //On réinitialise les champs du formulaire
      setSupportFile(null);

      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("Décision validée avec succès !"); //Toast de succès

      onClose(); // On ferme le modal
    } catch (error: any) {
      // Si erreur (Toast Error)
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // 👇 Gestion de l'ajout de la pièce jointe
  const handleAddSupport = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    setSupportFile(e.target.files[0]);
    e.target.value = ""; // reset
  };

  const handleRemoveSupport = () => setSupportFile(null);

  useEffect(() => {
    if (show) {
      reset({
        statut: "",
        validator: userData?.id,
        id_decision: idDecision,
        level_of_validation: validationLevel.toString(),
        observation: "",
      });

      setSupportFile(null);
    }
  }, [show, validationLevel]);

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton>
          <Modal.Title>Valider la décision</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Message d'erreur serveur */}
          {errors.root && (
            <div className="alert alert-danger rounded-pill alert-dismissible fade show">
              {errors.root.message}
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => clearErrors("root")}
              >
                <i className="fas fa-xmark" />
              </button>
            </div>
          )}
          <form
            id="level-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Validation de niveau {validationLevel}
                  </label>
                  <Select
                    allowClear
                    size="large"
                    optionFilterProp="label"
                    removeIcon={<span style={{ fontSize: 12 }}>×</span>}
                    style={{ width: "100%" }}
                    getPopupContainer={(trigger) =>
                      trigger.parentElement as HTMLElement
                    }
                    dropdownStyle={{ zIndex: 2000 }}
                    placeholder="Sélectionner le parcours et le cycle de l'étudiant"
                    value={statut || undefined}
                    onChange={(id) => {
                      setValue("statut", id || "");
                    }}
                  >
                    <Select.Option
                      value={"Validé"}
                      label={`Validé la décision`}
                    >
                      Validé la décision
                    </Select.Option>
                    <Select.Option
                      value={"Rejeté"}
                      label={`Rejeté la décision`}
                    >
                      Rejeté la décision
                    </Select.Option>
                  </Select>

                  {errors.statut && (
                    <div className="invalid-feedback">
                      {errors.statut.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Observation
                  </label>
                  <textarea
                    {...register("observation")}
                    className={
                      errors.observation
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: valide pour la session de Juin 2025"
                  />
                  {errors.observation && (
                    <div className="invalid-feedback">
                      {errors.observation.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12 d-flex align-items-center justify-content-between mb-3">
                <div>
                  {supportFile ? (
                    <span>{supportFile.name}</span>
                  ) : (
                    <span className="text-muted">
                      Aucun fichier importé pour le Support
                    </span>
                  )}
                </div>
                <div>
                  {supportFile ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={handleRemoveSupport}
                    >
                      <i className="ti ti-trash" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => supportFileInputRef.current?.click()}
                    >
                      <i className="ti ti-file-description me-1" />
                      Ajouter Support
                    </button>
                  )}
                  <input
                    ref={supportFileInputRef}
                    type="file"
                    className="d-none"
                    accept=".pdf,.doc,.docx"
                    onChange={handleAddSupport}
                  />
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              setSupportFile(null);
              onClose();
            }}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="level-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  ValidationModal,
  [buildPermission(Resource.DIPLOME, Action.READ)],
  "masquer",
);
